import React, { createContext, useContext, useRef, type PropsWithChildren } from 'react';
import { useSyncExternalStore } from 'react';

// --- Store ---
type HoverState = {
  overBar: boolean;
  overPop: boolean;
};

type HoverStore = {
  getSnapshot: () => HoverState;
  getInteractive: () => boolean;
  subscribe: (cb: () => void) => () => void;
  setOverBar: (v: boolean) => void;
  setOverPop: (v: boolean) => void;
};

// --- Store ---
function createHoverStore(): HoverStore {
  let state: HoverState = { overBar: false, overPop: false };
  const subs = new Set<() => void>();
  const getSnapshot = () => state;
  const getInteractive = () => state.overBar || state.overPop;

  const update = (key: keyof HoverState, v: boolean) => {
    if (state[key] !== v) {
      state = { ...state, [key]: v };
      subs.forEach((cb) => cb());
    }
  };

  return {
    getSnapshot,
    getInteractive,
    subscribe: (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
    setOverBar: (v) => update('overBar', v),
    setOverPop: (v) => update('overPop', v),
  };
}

// --- Context ---
const HoverCtx = createContext<HoverStore | null>(null);

export const HoverProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const storeRef = useRef<HoverStore>();
  if (!storeRef.current) storeRef.current = createHoverStore();

  return <HoverCtx.Provider value={storeRef.current}>{children}</HoverCtx.Provider>;
};

export function useHoverStore(): HoverStore {
  const ctx = useContext(HoverCtx);
  if (!ctx) throw new Error('useHoverStore must be used inside <HoverProvider>');
  return ctx;
}

// --- Hooks ---
export function useIsInteractive(): boolean {
  const store = useHoverStore();
  return useSyncExternalStore(store.subscribe, store.getInteractive, store.getInteractive);
}
